import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Users, HandHeart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [stats, setStats] = useState({ total_donations: 0, volunteers: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, volunteersRes] = await Promise.all([
          axios.get(`${API}/donations/stats`),
          axios.get(`${API}/volunteers/count`)
        ]);
        setStats({
          total_donations: statsRes.data.total_donations,
          volunteers: volunteersRes.data.count
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const impactStats = [
    { icon: Users, label: 'Volunteers', value: '200+', color: 'bg-terracotta' },
    { icon: HandHeart, label: 'Donations', value: stats.total_donations > 0 ? `${stats.total_donations}+` : '5000+', color: 'bg-saffron' },
    { icon: Heart, label: 'Lives Touched', value: '2500+', color: 'bg-saffron' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-cream hero-gradient overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-saffron/10 rounded-full">
                <span className="text-saffron font-body text-sm font-medium">For a Better Society</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl font-bold text-gray-900 leading-tight" data-testid="hero-title">
                Preserving Dharma,<br />
                <span className="text-saffron">Empowering Society</span>
              </h1>
              <p className="font-body text-lg text-gray-600 max-w-xl leading-relaxed">
                Join us in our sacred mission to build temples, preserve Hindu culture, and serve humanity through education, food donation, and community support.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/donate">
                  <Button 
                    className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-8 py-6 font-body font-semibold text-lg btn-lift shadow-lg hover:shadow-xl"
                    data-testid="hero-donate-button"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                </Link>
                <Link to="/volunteer">
                  <Button 
                    variant="outline"
                    className="border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full px-8 py-6 font-body font-semibold text-lg transition-all"
                    data-testid="hero-volunteer-button"
                  >
                    Become a Volunteer
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/15902534/pexels-photo-15902534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Temple Deity"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="bg-white py-24 md:py-28" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Our Mission</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
            Serving Dharma Through Action
          </h2>
          <p className="font-body text-lg text-gray-600 leading-relaxed">
            Dharma Astra Association is dedicated to the sacred mission of constructing temples, particularly our flagship Lord Ram Temple project, while simultaneously uplifting communities through education, women empowerment, food donation, and healthcare services.
          </p>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-cream-dark py-24 md:py-28" data-testid="impact-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Our Impact</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-4">Making a Difference</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactStats.map((stat, index) => (
              <Card key={index} className="bg-white rounded-2xl border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-10 text-center">
                  <div className={`w-18 h-18 ${stat.color} rounded-full flex items-center justify-center mx-auto mb-5`} style={{width: '4.5rem', height: '4.5rem'}}>
                    <stat.icon className="w-9 h-9 text-white" />
                  </div>
                  <p className="font-heading text-4xl md:text-5xl font-bold text-gray-900">{stat.value}</p>
                  <p className="font-body text-base text-gray-500 mt-3">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-saffron py-24 md:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Join Us in Our Sacred Mission
          </h2>
          <p className="font-body text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Your contribution, no matter how small, helps us build temples, educate children, feed the hungry, and empower communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-white text-saffron hover:bg-cream rounded-full px-8 py-6 font-body font-semibold text-lg btn-lift">
                <Heart className="w-5 h-5 mr-2" />
                Make a Donation
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-saffron rounded-full px-8 py-6 font-body font-semibold text-lg transition-all">
                Volunteer With Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
